/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiDisplayFieldConfig, UiDisplayFieldSubCommandHandler, UiDisplayFieldSubEventSource} from "../../generated/UiDisplayFieldConfig";
import {UiField} from "./UiField";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {escapeHtml} from "../Common";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiDisplayField extends UiField<UiDisplayFieldConfig, string> implements UiDisplayFieldSubEventSource, UiDisplayFieldSubCommandHandler {

	private _$field: JQuery;
	private showHtml: boolean;

	protected initialize(config: UiDisplayFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this._$field = $(`<div class="UiDisplayField">`);

		this.setShowBorder(config.showBorder);
		this.setShowHtml(config.showHtml);

		this._$field.appendTo(targetElement);
	}

	isValidData(v: string): boolean {
		return v == null || typeof v === "string";
	}

	setShowBorder(showBorder: boolean): void {
		this._$field.toggleClass("border", showBorder);
	}

	setShowHtml(showHtml: boolean): void {
		this.showHtml = showHtml;
		this.displayCommittedValue();
	}

	public getMainDomElement(): JQuery {
		return this._$field;
	}

	public getFocusableElement(): JQuery {
		return null;
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		if (uiValue) {
			if (this.showHtml) {
				this._$field.html(uiValue);
			} else {
				this._$field.text(uiValue);
			}
		} else {
			this._$field.empty();
		}
	}

	focus(): void {
		// do nothing
	}

	destroy(): void {
		this._$field.remove();
	}

	getTransientValue(): string {
		return this.getCommittedValue();
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		UiField.defaultOnEditingModeChangedImpl(this);
	}

	public static getReadOnlyHtml(config: UiDisplayFieldConfig, value: string, context: TeamAppsUiContext): string {
		let content: string;
		if (value != null && value) {
			if (config.showHtml) {
				content = value;
			} else {
				content = escapeHtml(value);
			}
		} else {
			content = "";
		}
		return `<div class="static-readonly-UiDisplayField">${content}</div>`;
	}

	getDefaultValue() {
		return "";
	}

	public valuesChanged(v1: string, v2: string): boolean {
		return false;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiDisplayField", UiDisplayField);